import type { ComboboxOption } from '@repo/design-system';
import type { CompositeRow } from '@repo/ui';
import { asArray, matchPlanEntities, planSearchEntry } from '@repo/front-end-utils';
import type { PlanSearchEntry } from '@repo/front-end-utils';
import type { FloorPlannerComposite, FloorPlannerLayout } from '@repo/frontend-types';

import { automaticRowRefid } from './compositeEditing';
import { decodeRef, encodeRef, labelForRef } from './compositeRefOptions';
import type { AutomaticTotal } from './entityCoverage';

/**
 * The Composite measures step's search (`composites-page-search`, spec FR3): **a hit is a total**,
 * found by its own target or by any of its sources.
 *
 * It does not have a matcher of its own. The words are matched against the plan THINGS — the twin,
 * its floors, its areas and machines — by the one shared matcher the Floor plan and Observers steps
 * use (`matchPlanEntities`), and each matched thing is then mapped to the totals that use it. So the
 * same words find the same things on all three steps, and the shared matcher's rules hold here
 * unchanged: every word must be found in ONE thing, and a code is matched by its tail.
 *
 * Why not one entry per total with all its sources' words in it: that would let "cardio studio"
 * match the whole-twin total because one source says "Cardio" and another "Studio", and it would
 * lose code matching for sources, because an entry has one code.
 */

/** A total's references, encoded (`encodeRef`) — what the search maps matched things onto. */
export interface CompositeTotalRefs {
  target: string;
  /**
   * Its sources in stored order — index-aligned with the row's `inputs`, whose labels the chips
   * draw. A malformed input encodes to `''`, which matches nothing. Empty for an automatic total.
   */
  sources: string[];
}

/** One result: the total's own row, how it was found, and which of its sources matched. */
export interface CompositeSearchHit {
  row: CompositeRow;
  via: 'target' | 'source';
  /** The matching sources' labels, in the total's order; empty on a target hit. */
  matched: string[];
}

/**
 * Every total the step lists, by its row's refid: the written totals by their own refid, and the
 * automatic ones by the key the step gives their rows (`automaticRowRefid`). The *own cameras* flag
 * is not a source here — it names nothing on the plan.
 */
export function compositeTotalRefs(
  live: ReadonlyArray<FloorPlannerComposite>,
  automatic: ReadonlyArray<AutomaticTotal>,
): Map<string, CompositeTotalRefs> {
  const out = new Map<string, CompositeTotalRefs>();
  for (const c of live) {
    out.set(c.refid, { target: encodeRef(c.target), sources: asArray(c.inputs).map(encodeRef) });
  }
  for (const total of automatic) {
    const target = encodeRef(total.target);
    if (target) out.set(automaticRowRefid(target), { target, sources: [] });
  }
  return out;
}

/**
 * The encoded reference a search entry stands for, or `null` for one no total can point at (a
 * camera, a feed). Areas and machines encode from their kind; the floor and twin entries carry their
 * encoded reference as their `refid`.
 */
function refKeyOf(entry: PlanSearchEntry): string | null {
  if (entry.kind === 'surface' || entry.kind === 'item')
    return encodeRef({ kind: 'entity', refid: entry.refid, entityKind: entry.kind });
  if (entry.kind === 'floor' || entry.kind === 'twin') return entry.refid;
  return null;
}

/**
 * Everything a total can point at, as search entries:
 *
 * - the areas and machines of `index` (the shared joined index — register and catalogue fields
 *   included), each with the label THIS step shows for it added to its fields, so whatever a row and
 *   its chips say is always findable, even where the register-joined name differs;
 * - the whole twin, and every floor with a numeric id (the rule `compositeRefOptions` applies);
 * - every reference a total in `totals` uses that the plan no longer holds, labelled as its row
 *   shows it ("… no longer on the plan"), so that total can still be found.
 *
 * ⚠ A total's sources are NOT folded into its target's entry: every word must be found in one
 * thing, and a total is reached through a source by `matchCompositeTotals`, not by its fields.
 */
export function compositeRefEntries(
  index: ReadonlyArray<PlanSearchEntry>,
  {
    layout,
    options,
    totals,
  }: {
    layout: FloorPlannerLayout | null | undefined;
    options: ReadonlyArray<ComboboxOption>;
    totals: ReadonlyMap<string, CompositeTotalRefs>;
  },
): PlanSearchEntry[] {
  const optionLabel = new Map(options.map(o => [o.value, o.label] as const));
  const labelOf = (key: string): string | undefined => optionLabel.get(key);
  const out: PlanSearchEntry[] = [];
  const known = new Set<string>();

  for (const entry of index) {
    const key = refKeyOf(entry);
    if (!key) continue;
    known.add(key);
    const shown = labelOf(key)?.trim().toLowerCase();
    out.push(
      shown && !entry.fields.includes(shown)
        ? { ...entry, fields: [...entry.fields, shown] }
        : entry,
    );
  }

  const twinLabel = labelOf('twin') ?? 'Whole twin';
  known.add('twin');
  out.push(
    planSearchEntry({
      refid: 'twin',
      kind: 'twin',
      floorIndex: null,
      floorName: null,
      label: twinLabel,
      code: '',
      detail: '',
      searchable: [twinLabel, 'whole twin'],
    }),
  );

  asArray(layout?.floors).forEach((floor, floorIndex) => {
    if (typeof floor?.id !== 'number') return;
    const key = encodeRef({ kind: 'floor', floorId: floor.id });
    const label = labelOf(key) ?? (floor.name?.trim() || `Floor ${floor.id}`);
    known.add(key);
    out.push(
      planSearchEntry({
        refid: key,
        kind: 'floor',
        floorIndex,
        floorName: label,
        label,
        code: '',
        detail: '',
        searchable: [label, 'floor'],
      }),
    );
  });

  const refsInUse: string[] = [];
  totals.forEach(t => refsInUse.push(t.target, ...t.sources));
  for (const key of refsInUse) {
    // Only what the plan no longer holds: anything the step's options name has its entry above.
    if (known.has(key) || optionLabel.has(key)) continue;
    const ref = decodeRef(key);
    if (!ref) continue;
    known.add(key);
    const { label } = labelForRef(ref, options);
    out.push(
      planSearchEntry({
        refid: ref.kind === 'entity' ? ref.refid : key,
        kind: ref.kind === 'entity' ? ref.entityKind : ref.kind,
        floorIndex: null,
        floorName: null,
        label,
        code: '',
        detail: '',
        searchable: [label],
      }),
    );
  }

  return out;
}

/**
 * The totals a query finds, best first: totals found by their own target, then totals found through
 * a source; within each, by how well the matched thing ranked in the shared matcher; ties keep the
 * list's order. `total` counts every hit, so a capped list can say how many it is not showing.
 */
export function matchCompositeTotals(
  query: string,
  {
    entries,
    totals,
    rows,
    twinBid,
    limit = 50,
  }: {
    entries: ReadonlyArray<PlanSearchEntry>;
    totals: ReadonlyMap<string, CompositeTotalRefs>;
    rows: ReadonlyArray<CompositeRow>;
    twinBid?: string | null;
    limit?: number;
  },
): { hits: CompositeSearchHit[]; total: number } {
  const { hits: things } = matchPlanEntities(entries, query, {
    twinBid,
    limit: Number.POSITIVE_INFINITY,
  });
  if (things.length === 0) return { hits: [], total: 0 };
  const rank = new Map<string, number>();
  things.forEach((entry, i) => {
    const key = refKeyOf(entry);
    if (key && !rank.has(key)) rank.set(key, i);
  });

  const scored: Array<CompositeSearchHit & { rank: number; order: number }> = [];
  rows.forEach((row, order) => {
    const refs = totals.get(row.refid);
    if (!refs) return;
    const targetRank = rank.get(refs.target);
    if (targetRank !== undefined) {
      scored.push({ row, via: 'target', matched: [], rank: targetRank, order });
      return;
    }
    /*
     * One entry per matched REFERENCE — two areas both called "Rack 6" are two sources — in the
     * row's own order, labelled as its chips are (`row.inputs`, index-aligned with `sources`). A
     * reference listed twice in one total is counted once.
     */
    const seen = new Set<string>();
    const matched: string[] = [];
    let best = Number.POSITIVE_INFINITY;
    refs.sources.forEach((key, i) => {
      const at = rank.get(key);
      if (at === undefined || seen.has(key)) return;
      seen.add(key);
      matched.push(row.inputs[i]?.label ?? key);
      best = Math.min(best, at);
    });
    if (matched.length === 0) return;
    scored.push({ row, via: 'source', matched, rank: best, order });
  });

  scored.sort(
    (a, b) =>
      (a.via === 'target' ? 0 : 1) - (b.via === 'target' ? 0 : 1) ||
      a.rank - b.rank ||
      a.order - b.order,
  );

  return {
    hits: scored.slice(0, limit).map(({ row, via, matched }) => ({ row, via, matched })),
    total: scored.length,
  };
}

/**
 * Whether the list's totals are all in yet (`composites-page-search`, Copilot on #1943). Some of a
 * twin's totals reach the plan only when its running configuration has been read and matched, so
 * an empty list — or no match — while that read is in flight or has failed is NOT "this twin has
 * no totals": the box must say the read is unfinished rather than make that claim. `settled` when
 * the step reads no configuration, the twin has none, or both reads have succeeded.
 */
export type TotalsReadState = 'settled' | 'reading' | 'failed';

export function totalsReadState(
  live?: {
    config: { status: string; id?: number };
    statement: { status: string; value?: { configId?: number } | null };
    compositeMatch?: unknown;
  } | null,
): TotalsReadState {
  if (!live) return 'settled';
  const { config, statement, compositeMatch } = live;
  if (config.status === 'failed' || statement.status === 'failed') return 'failed';
  if (config.status === 'none') return 'settled';
  /*
   * Ready reads are not enough: the totals are placed on the plan only once the statement — the
   * CURRENT configuration's — has been matched (`compositeMatch`), which can wait on the camera
   * streams. The page's own generation gate (`generationSettled`) tests exactly this; so does this.
   */
  if (
    config.status === 'ready' &&
    statement.status === 'ready' &&
    statement.value?.configId === config.id &&
    compositeMatch != null
  )
    return 'settled';
  return 'reading';
}
